import { EVENT_FILTER_CONFIG, createBeforeSend } from './eventFilter';

import APP from 'constants/app';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

// NOTE: NODE_ENV === 'production' is true for BOTH production and
// release builds. Do NOT use it to distinguish prod from release.
// Use PRODUCTION_HOSTNAME (runtime hostname check) instead.
const DEV_TOOLBAR_STORAGE_KEY = '__dev_toolbar';
const PRODUCTION_HOSTNAME = APP.SHORTENED_URL;

let posthogClient = null;
let initPromise = null;

/**
 * Returns the PostHog client synchronously if already initialized, or null.
 * Call initPostHog() first to load the SDK asynchronously.
 */
export const getPostHogClient = () => posthogClient;

/**
 * Check if PostHog is disabled via DevToolbar toggle.
 * Only applies on non-production hosts (runtime hostname check).
 */
const isPostHogDisabledByToolbar = () => {
  if (typeof window === 'undefined') return false;

  const { hostname } = window.location;

  if (
    hostname === PRODUCTION_HOSTNAME ||
    hostname === `www.${PRODUCTION_HOSTNAME}`
  ) {
    return false;
  }

  try {
    const stored = JSON.parse(localStorage.getItem(DEV_TOOLBAR_STORAGE_KEY));
    return stored?.posthogDisabled === true;
  } catch {
    return false;
  }
};

/**
 * Lazily loads posthog-js and initializes the client. Returns a promise
 * that resolves to the PostHog client, or null if unavailable.
 * Subsequent calls return the same promise. Keeps posthog-js out of
 * the main _app bundle (~100KB gzipped).
 */
export const initPostHog = () => {
  if (
    typeof window === 'undefined' ||
    !POSTHOG_KEY ||
    isPostHogDisabledByToolbar()
  ) {
    return Promise.resolve(null);
  }

  if (posthogClient) {
    return Promise.resolve(posthogClient);
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: false, // We handle pageviews manually via router events
        capture_pageleave: false,
        heatmaps: false,
        before_send: createBeforeSend(EVENT_FILTER_CONFIG),
        persistence: 'localStorage+cookie',
        opt_in_site_apps:
          window.location.hostname !== PRODUCTION_HOSTNAME &&
          window.location.hostname !== `www.${PRODUCTION_HOSTNAME}`,
        autocapture: {
          url_allowlist: ['/cupons/loja/.*'],
        },
        loaded: (ph) => {
          if (process.env.NODE_ENV === 'development') {
            ph.debug();
            window.posthog = ph;
          }

          // Apply DevToolbar persisted overrides immediately so
          // flag reads by usePostHogExperiment get the correct
          // value on the first call — before any .then() runs.
          try {
            const stored = JSON.parse(
              localStorage.getItem(DEV_TOOLBAR_STORAGE_KEY)
            );

            if (stored?.flagOverrides) {
              const keys = Object.keys(stored.flagOverrides);

              if (keys.length > 0) {
                ph.featureFlags.overrideFeatureFlags({
                  flags: stored.flagOverrides,
                });
              }
            }
          } catch {
            // Ignore — localStorage may be unavailable
          }
        },
      });

      posthogClient = posthog;
      return posthog;
    })
    .catch(() => {
      // Ad blockers or network errors may block posthog-js. Reset so
      // future navigation can retry. Resolve to null so consumers
      // gracefully fall back to control variant.
      initPromise = null;
      return null;
    });

  return initPromise;
};
